/**
 * Client-side wallet activity for transfers that have no index canister
 * (ICP, ckBTC, ckETH, ckUSDC) or when index is not configured.
 * Merged with protocol_flow + ledger index in useWalletActivity.
 */

import type { ActivityEntry } from "@/lib/activity";

const PREFIX = "motokomaker.v2.clientActivity.";
const MAX_ENTRIES = 80;

export function loadClientActivity(principal: string): ActivityEntry[] {
	if (!principal || typeof localStorage === "undefined") return [];
	try {
		const raw = localStorage.getItem(PREFIX + principal);
		if (!raw) return [];
		const arr = JSON.parse(raw) as Array<
			Omit<ActivityEntry, "timestampNs" | "amountE8" | "vaultId"> & {
				timestampNs: string;
				amountE8: string | null;
				vaultId: string | null;
			}
		>;
		return arr.map((e) => ({
			...e,
			timestampNs: BigInt(e.timestampNs),
			amountE8: e.amountE8 != null ? BigInt(e.amountE8) : null,
			vaultId: e.vaultId != null ? BigInt(e.vaultId) : null,
			fromAccount:
				(e as { fromAccount?: string | null }).fromAccount ??
				(e.direction === "out" ? principal : e.counterparty) ??
				null,
			toAccount:
				(e as { toAccount?: string | null }).toAccount ??
				(e.direction === "in" ? principal : e.counterparty) ??
				null,
		}));
	} catch {
		return [];
	}
}

function persist(principal: string, entries: ActivityEntry[]) {
	if (!principal || typeof localStorage === "undefined") return;
	const serializable = entries.slice(0, MAX_ENTRIES).map((e) => ({
		...e,
		timestampNs: e.timestampNs.toString(),
		amountE8: e.amountE8 != null ? e.amountE8.toString() : null,
		vaultId: e.vaultId != null ? e.vaultId.toString() : null,
	}));
	localStorage.setItem(PREFIX + principal, JSON.stringify(serializable));
}

export function recordClientTransfer(opts: {
	principal: string;
	asset: string;
	amount: number;
	direction: "in" | "out";
	counterparty?: string | null;
	txRef?: string;
	detail?: string;
}): ActivityEntry {
	const cp = opts.counterparty ?? null;
	const fromAccount = opts.direction === "out" ? opts.principal : cp;
	const toAccount = opts.direction === "in" ? opts.principal : cp;
	const entry: ActivityEntry = {
		id: `client:${Date.now()}:${opts.asset}:${opts.amount}`,
		timestampNs: BigInt(Date.now()) * 1_000_000n,
		category: "transfer",
		type: opts.direction === "in" ? "TransferIn" : "TransferOut",
		asset: opts.asset,
		amount: opts.amount,
		amountE8: null,
		direction: opts.direction,
		counterparty: cp,
		fromAccount,
		toAccount,
		vaultId: null,
		txRef: opts.txRef ?? "local",
		status: "confirmed",
		source: "client",
		detail:
			opts.detail ??
			`${opts.direction === "out" ? "Sent" : "Received"} ${opts.asset} from this device`,
	};
	const prev = loadClientActivity(opts.principal);
	persist(opts.principal, [entry, ...prev]);
	return entry;
}
